import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Spinner,
} from '@billda/design-system';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreatePlatformOrganisation } from '../../hooks/useCreatePlatformOrganisation';

const schema = yup.object({
  organisationName: yup
    .string()
    .required('Organisation name is required')
    .max(100),
  ownerEmail: yup
    .string()
    .required('Email is required')
    .email('Must be a valid email')
    .max(100),
  givenName: yup.string().required('Given name is required').max(100),
  familyName: yup.string().required('Family name is required').max(100),
});

type FormData = yup.InferType<typeof schema>;

interface CreateOrganisationDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CreateOrganisationDialog: React.FC<
  CreateOrganisationDialogProps
> = ({ open, onClose }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      organisationName: '',
      ownerEmail: '',
      givenName: '',
      familyName: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    mutate: createOrganisation,
    isPending,
    error,
    reset: resetMutation,
  } = useCreatePlatformOrganisation();

  const handleClose = () => {
    reset();
    resetMutation();
    onClose();
  };

  const onSubmit = (data: FormData) => {
    createOrganisation(data, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="max-w-lg"
        data-testid="create-organisation-dialog"
      >
        <DialogHeader>Create Organisation</DialogHeader>
        {error && (
          <Alert
            severity="error"
            className="mb-4 mt-2"
            data-testid="create-organisation-error"
          >
            {error.message}
          </Alert>
        )}

        <Controller
          name="organisationName"
          control={control}
          render={({ field, fieldState }) => (
            <div className="mb-4">
              <Field
                label="Organisation Name"
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  invalid={!!errors.organisationName}
                  autoFocus
                  data-testid="create-organisation-name"
                />
              </Field>
            </div>
          )}
        />

        <Controller
          name="ownerEmail"
          control={control}
          render={({ field, fieldState }) => (
            <div className="mb-4">
              <Field label="Owner Email" error={fieldState.error?.message}>
                <Input
                  {...field}
                  type="email"
                  invalid={!!errors.ownerEmail}
                  data-testid="create-organisation-email"
                />
              </Field>
            </div>
          )}
        />

        <Controller
          name="givenName"
          control={control}
          render={({ field, fieldState }) => (
            <div className="mb-4">
              <Field label="Given Name" error={fieldState.error?.message}>
                <Input
                  {...field}
                  invalid={!!errors.givenName}
                  data-testid="create-organisation-given-name"
                />
              </Field>
            </div>
          )}
        />

        <Controller
          name="familyName"
          control={control}
          render={({ field, fieldState }) => (
            <div className="mb-4">
              <Field label="Family Name" error={fieldState.error?.message}>
                <Input
                  {...field}
                  invalid={!!errors.familyName}
                  data-testid="create-organisation-family-name"
                />
              </Field>
            </div>
          )}
        />
        <DialogFooter className="px-0 pb-0">
          <Button variant="ghost" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="neutral"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending}
            data-testid="create-organisation-submit"
          >
            {isPending ? <Spinner size={20} /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
